import React, { createContext, useContext, useMemo } from 'react';
import { Category } from '@/types/category';
import { csvProductService } from '@/services/csv/CsvProductService';
import { useQuery } from '@tanstack/react-query';

interface CategoryContextType {
  categories: Map<string, Category>;
  getCategoryName: (id: string | number) => string;
  isLoading: boolean;
}

const CategoryContext = createContext<CategoryContextType | null>(null);

// Fetch all categories including system ones
const fetchAllCategories = async (): Promise<Map<string, Category>> => {
  const allCategories = csvProductService.getAvailableCategories(true);
  const categoryMap = new Map<string, Category>();
  
  allCategories.forEach(cat => {
    categoryMap.set(cat.id, cat);
  });
  
  return categoryMap;
};

export const CategoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use TanStack Query for category data
  const { data: categories = new Map(), isLoading } = useQuery({
    queryKey: ['allCategories'],
    queryFn: fetchAllCategories,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    enabled: csvProductService.hasCsvData(),
    refetchOnWindowFocus: false,
  });

  const getCategoryName = useMemo(() => {
    return (id: string | number): string => {
      const categoryId = String(id);
      const category = categories.get(categoryId);
      
      if (!category) {
        return `Category ${categoryId}`;
      }
      
      // Get language preference
      const lang = localStorage.getItem('knitido_language') || 'en';
      
      // Return appropriate language version
      if (lang === 'de' && category.nameDE) return category.nameDE;
      if (lang === 'en' && category.nameEN) return category.nameEN || category.name;
      if (lang === 'ja' && category.nameJA) return category.nameJA || category.name;
      
      // Default to German name or generic name
      return category.nameDE || category.name || `Category ${categoryId}`;
    };
  }, [categories]);

  return (
    <CategoryContext.Provider value={{ categories, getCategoryName, isLoading }}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within CategoryProvider');
  }
  return context;
};